import React, { useCallback, useEffect, useState } from "react";

import { getIn, useFormikContext } from "formik";
import * as Yup from "yup";

import FinanceAndAccounting from "components/Business/BusinessForm/FinanceAndAccounting";
import { GeneralCollapse } from "components/Business/BusinessForm/GeneralCollapse";
import BusinessRelationshipsTable from "components/Business/BusinessForm/Relationships/BusinessRelationshipsTable";
import { withNamespace } from "components/Form/FormikControls";

import { BusinessModalSection } from "constants/navigation";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";

import {
  useCanLinkExternalIntegrationPermission,
  useHasBillingPermission,
  useHasDeploymentPermission,
  useHasSaleyardOrDeploymentPermission,
} from "hooks";

import BusinessTransactions from "components/Business/BusinessForm/Transactions";
import { useDispatch } from "react-redux";
import { toggleBusinessModalSection } from "actions/modals/business";
import BusinessAddressTable from "./BusinessAddressTable";
import {
  BuyerWaysHeader,
  BuyerWaysTable,
  BuyerWayValidationSchema,
} from "./BuyerWays/BuyerWaysTable";
import DeploymentBusinessTable from "./DeploymentBusinessTable";
import { EmailRecipientsPanel } from "./EmailRecipients/EmailRecipientsPanel";
import { IntegrationsTable } from "./Integrations/IntegrationsTable";
import MergedBusinessesComponent from "./MergedBusinesses/MergedBusinessesComponent";
import {
  RegistrationLookupContext,
  useRegistrationLookup,
} from "./FinanceAndAccounting/useRegistrationLookup";
import { PropertyValidationSchema } from "./Properties/DeploymentPropertyForm";
import {
  PropertiesHeader,
  PropertiesTable,
} from "./Properties/PropertiesTable";
import { BusinessFormValues } from "./types";

interface DeploymentBusinessFormProps {
  setIsSubFormActive: (isActive: boolean) => void;
  business: Business | null;
  section?: string;
  namespace?: string;
  defaultAddressSearch?: string;
  openSideLoadedBusinessModal?: boolean;
  sideLoadedBusinessId?: string;
  onSideLoadedSuggestionsCancel?: () => void;
}

const BuyerWayTableValidationSchema = Yup.object().shape({
  editingValue: BuyerWayValidationSchema.nullable(),
});

const PropertiesTableValidationSchema = Yup.object().shape({
  editingValue: PropertyValidationSchema.nullable(),
});

export const DeploymentBusinessForm = (
  props: DeploymentBusinessFormProps,
): React.JSX.Element => {
  const {
    setIsSubFormActive,
    business,
    namespace: ns = "",
    defaultAddressSearch,
    section = BusinessModalSection.GENERAL,
    openSideLoadedBusinessModal,
    sideLoadedBusinessId,
    onSideLoadedSuggestionsCancel,
  } = props;
  const [isEditingProperty, setIsEditingProperty] = useState<boolean>(false);
  const [isEditingBuyerWay, setIsEditingBuyerWay] = useState<boolean>(false);

  const formikProps = useFormikContext<BusinessFormValues>();

  const dispatch = useDispatch();

  const {
    abn: abnValue,
    id: businessIdValue,
    abrLastChecked: abrLastCheckedValue,
  } = formikProps.values;

  const registrationLookup = useRegistrationLookup(ns);
  const { isLookingUp: isLookingUpRegistration, lookupError } =
    registrationLookup;

  useEffect(() => {
    setIsSubFormActive(
      Boolean(
        isEditingProperty ||
          isEditingBuyerWay ||
          // Saving now would keep the previous registration.
          isLookingUpRegistration ||
          lookupError,
      ),
    );
  }, [
    isEditingProperty,
    isEditingBuyerWay,
    isLookingUpRegistration,
    lookupError,
    setIsSubFormActive,
  ]);

  const editProperty = (): void => {
    setIsEditingProperty(true);
  };

  const onSetPrimaryContactEmail = (emailAddress: string | null): void => {
    const { setFieldValue, setFieldTouched } = formikProps;
    const field = "primaryContactEmail";
    setFieldValue(field, emailAddress);
    setFieldTouched(field);
  };

  const toggleSection = useCallback(
    newSection => dispatch(toggleBusinessModalSection(newSection)),
    [dispatch],
  );

  const toggleAddress = (): void => {
    toggleSection(BusinessModalSection.ADDRESS);
  };

  const toggleGeneral = (): void => {
    toggleSection(BusinessModalSection.GENERAL);
  };

  const toggleProperties = (): void => {
    toggleSection(BusinessModalSection.PROPERTIES);
  };

  const toggleIntegrations = (): void => {
    toggleSection(BusinessModalSection.INTEGRATIONS);
  };

  const toggleBuyerWays = (): void => {
    toggleSection(BusinessModalSection.BUYER_WAYS);
  };

  const toggleEmailRecipients = (): void => {
    toggleSection(BusinessModalSection.EMAIL_RECIPIENTS);
  };

  const toggleBusinessRelationships = (): void => {
    toggleSection(BusinessModalSection.BUSINESS_RELATIONSHIPS);
  };

  const toggleTransactions = (): void => {
    toggleSection(BusinessModalSection.TRANSACTIONS);
  };

  const toggleFinanceAndAccounting = (): void => {
    toggleSection(BusinessModalSection.FINANCE_AND_ACCOUNTING);
  };

  const toggleBusinessMergeEvents = (): void => {
    toggleSection(BusinessModalSection.BUSINESS_MERGE_EVENTS);
  };

  const buyerWaysList = getIn(
    formikProps.values,
    withNamespace(ns, "buyerWays"),
  );

  const propertiesList = getIn(
    formikProps.values,
    withNamespace(ns, "properties"),
  );

  const primaryContactEmail = getIn(
    formikProps.values,
    withNamespace(ns, "primaryContactEmail"),
  );

  const hasBusinessFinanceFeature = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureBusinessFinance,
    SaleyardPermission.featureBusinessFinance,
  );

  const hasBillingFeature = useHasBillingPermission();

  const canLinkExternalIntegration = useCanLinkExternalIntegrationPermission();

  const hasBusinessRelationFeature = useHasDeploymentPermission(
    DeploymentPermission.featureBusinessRelations,
  );

  const hasfeatureOrganisationalMergeBusinessFeature =
    useHasSaleyardOrDeploymentPermission(
      DeploymentPermission.featureOrganisationalMergeBusiness,
      SaleyardPermission.featureOrganisationalMergeBusiness,
    );

  return (
    <RegistrationLookupContext.Provider value={registrationLookup}>
      <GeneralCollapse
        isOpen={section === BusinessModalSection.GENERAL}
        onToggle={toggleGeneral}
        namespace={ns}
        businessId={business?.id}
        openSideLoadedBusinessModal={openSideLoadedBusinessModal}
        sideLoadedBusinessId={sideLoadedBusinessId}
        onSideLoadedSuggestionsCancel={onSideLoadedSuggestionsCancel}
      />
      <BusinessAddressTable
        isOpen={section === BusinessModalSection.ADDRESS}
        onToggle={toggleAddress}
        namespace={withNamespace(ns, "address")}
        defaultAddressSearch={defaultAddressSearch}
        businessId={business?.id}
      />
      {hasBusinessFinanceFeature && (
        <FinanceAndAccounting
          isOpen={section === BusinessModalSection.FINANCE_AND_ACCOUNTING}
          onToggle={toggleFinanceAndAccounting}
          namespace={ns}
          abn={abnValue}
          businessId={businessIdValue}
          abrLastChecked={abrLastCheckedValue}
        />
      )}

      {hasBillingFeature && (
        <BusinessTransactions
          businessId={businessIdValue}
          isOpen={section === BusinessModalSection.TRANSACTIONS}
          onToggle={toggleTransactions}
        />
      )}
      <DeploymentBusinessTable
        businessId={business?.id}
        initialValuesList={propertiesList}
        isOpen={section === BusinessModalSection.PROPERTIES}
        namespace={withNamespace(ns, "properties")}
        onStartEditing={editProperty}
        onEndEditing={(): void => {
          setIsEditingProperty(false);
        }}
        onToggle={toggleProperties}
        headerComponent={PropertiesHeader}
        tableComponent={PropertiesTable}
        validationSchema={PropertiesTableValidationSchema}
        dataTour="propertyCollapse"
      />
      {canLinkExternalIntegration && (
        <IntegrationsTable
          isOpen={section === BusinessModalSection.INTEGRATIONS}
          onToggle={toggleIntegrations}
          businessId={business?.id}
        />
      )}
      <DeploymentBusinessTable
        businessId={business?.id}
        initialValuesList={buyerWaysList}
        isOpen={section === BusinessModalSection.BUYER_WAYS}
        namespace={withNamespace(ns, "buyerWays")}
        onStartEditing={(): void => {
          setIsEditingBuyerWay(true);
        }}
        onEndEditing={(): void => {
          setIsEditingBuyerWay(false);
        }}
        onToggle={toggleBuyerWays}
        headerComponent={BuyerWaysHeader}
        tableComponent={BuyerWaysTable}
        validationSchema={BuyerWayTableValidationSchema}
        dataTour="buyerWayCollapse"
        propertiesList={propertiesList}
      />
      <EmailRecipientsPanel
        businessId={business?.id}
        isOpen={section === BusinessModalSection.EMAIL_RECIPIENTS}
        namespace={ns}
        onSetPrimaryContactEmail={onSetPrimaryContactEmail}
        onToggle={toggleEmailRecipients}
        primaryContactEmail={primaryContactEmail}
      />
      {hasBusinessRelationFeature && (
        <BusinessRelationshipsTable
          isOpen={section === BusinessModalSection.BUSINESS_RELATIONSHIPS}
          onToggle={toggleBusinessRelationships}
          namespace={ns}
        />
      )}
      {hasfeatureOrganisationalMergeBusinessFeature && (
        <MergedBusinessesComponent
          businessId={business?.id}
          onToggle={toggleBusinessMergeEvents}
          isOpen={section === BusinessModalSection.BUSINESS_MERGE_EVENTS}
        />
      )}
    </RegistrationLookupContext.Provider>
  );
};
